import { comDirecao, direcaoParaAlvo } from '../../dominio/tabletop/direcao'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { perfilVeiculoPorId, type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import {
  PROTECOES_INICIAIS,
  type FerramentaVidro,
  type IdVidro,
  type ResultadoAcaoVidro,
} from '../../dominio/didatico/manejoVidros'
import type { EquipamentoEstabilizacao } from '../../dominio/didatico/estabilizacao'
import {
  clamparParametro,
  TRANSFORMACAO_TABLETOP_PADRAO,
  adicionarObjeto,
  adicionarAmassadoNoPonto,
  limparAmassadosDoVeiculo,
  ancorasLivresDoVeiculo,
  aplicarDeformacaoNaRegiao,
  atualizarObjeto,
  atualizarTransformacao,
  calcularDeformacaoFrontalPredefinida,
  deformacaoPredefinidaNaPorta,
  criarCenaPadrao,
  criarCenaPresetSalvamento,
  criarCenaVazia,
  criarPresetTabletopComplexo,
  encaixarPosicaoXZ,
  obterPresetPorId,
  desfazer as desfazerHistorico,
  desserializarCenaTabletop,
  desvincularParaOSolo,
  duplicarObjeto,
  executarCorteNoVeiculo,
  executarManejoVidroNoVeiculo,
  filaDeAtendimento,
  historicoVazio,
  moverNaFila as moverNaFilaDominio,
  podeDesfazer as podeDesfazerHistorico,
  podeRefazer as podeRefazerHistorico,
  poseDaPosicaoFinal,
  refazer as refazerHistorico,
  registrar,
  removerObjeto,
  renomearCena,
  restaurarEstruturaDoVeiculo,
  restaurarVidrosDoVeiculo,
  selecionarPecaNoVeiculo,
  serializarCenaTabletop,
  soltarVitimasDoVeiculoRemovido,
  vincularVitimaAoVeiculo,
  type BlocoRiscoTabletop,
  type BlocoVeiculoTabletop,
  type IdPortaTabletop,
  type BlocoVitimaTabletop,
  type CenaTabletop,
  type EstadoCortePecaTabletop,
  type Historico,
  type ObjetoTabletop,
  type ParametrosTabletop,
  type PosicaoFinalVeiculo,
  type PosturaVitimaSolo,
  type ResultadoIntervencaoVeiculoTabletop,
  type TipoObjetoTabletop,
  type TransformacaoTabletop,
  marcarItemChecklistAltura,
  type MarcacaoChecklistAltura,
  novoEnvolvimentoEm,
  acomodarPoliasNasCordas,
  alinharPoliaComCorda,
  juntarFitasEmPonto,
  podeJuntarFitas,
} from '../../dominio/tabletop'
import type { NivelDanoVeicular, RegiaoDanoVeicular } from '../../dominio/didatico/danoVeicular'
import { MATERIAIS_PADRAO_DERRAMAMENTO } from '../../dominio/tabletop/riscosDaCena'
import type { AcionamentoCobom } from '../../dominio/tabletop/tipos'
import { encaixarPecaNoPontoMaisPerto, fitaEmVComMosquetao, prenderPecaNoPonto, type PecaParaPrender } from '../../dominio/tabletop/prenderNoPonto'
import type { ReferenciaPonto } from '../../dominio/tabletop/tipos'
import { localizarPontoNoMundo } from '../../dominio/tabletop/ancoragem'
import type { FerramentaTabletop, PedidoTesteBalanco } from '../../visualizacao3d/tabletop/CenaTabletop'
import { useAncoragensTabletop } from './useAncoragensTabletop'
import { useCordasTabletop } from './useCordasTabletop'
import { tocarSomCorteMetal, tocarSomEstilhacoVidro } from '../audio/somOperacional'

const CHAVE_TABLETOP_LOCAL = 'csv-lab-tabletop-v2'
const CHAVE_TABLETOP_LEGADA = 'csv-lab-diorama-v1'

/** Sem cena salva (primeiro acesso, armazenamento bloqueado ou arquivo ilegível), o Table top abre no cenário padrão. */
function carregarTabletopLocal(): CenaTabletop {
  if (typeof window === 'undefined') return criarCenaPadrao()
  try {
    const salvo = window.localStorage.getItem(CHAVE_TABLETOP_LOCAL) ?? window.localStorage.getItem(CHAVE_TABLETOP_LEGADA)
    // a polia pendurada se acomoda na corda que passa por ela (cenas salvas antes disso)
    return salvo ? acomodarPoliasNasCordas(desserializarCenaTabletop(salvo)) : criarCenaPadrao()
  } catch {
    return criarCenaPadrao()
  }
}

/** Apaga a cena guardada no navegador (a atual e a do formato antigo); sem armazenamento, não há o que apagar. */
function apagarTabletopLocal() {
  try {
    window.localStorage.removeItem(CHAVE_TABLETOP_LOCAL)
    window.localStorage.removeItem(CHAVE_TABLETOP_LEGADA)
  } catch {
    // sem armazenamento: nada a apagar
  }
}

function novoIdTabletop(): string {
  return globalThis.crypto?.randomUUID?.() ?? `tabletop-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const TIPOS_VEICULO_NO_CATALOGO: readonly TipoObjetoTabletop[] = ['carro', 'suv']
const TIPOS_EQUIPAMENTO_NO_CATALOGO: readonly TipoObjetoTabletop[] = ['calco', 'cunha', 'vstrut', 'fitaCatraca', 'bloco']

/**
 * Blocos que dão sentido de imediato ao objeto recém-inserido: um carro já
 * nasce com posição final e proteções padrão (para a estabilização e o
 * manejo de vidros por veículo terem o que avaliar), uma vítima já nasce com
 * triagem amarela no solo (o instrutor reclassifica e vincula ao assento
 * depois), e um equipamento de estabilização já carrega seu próprio tipo.
 */
function blocoPadraoTabletop(tipo: TipoObjetoTabletop): Pick<ObjetoTabletop, 'veiculo' | 'vitima' | 'equipamento' | 'risco' | 'cor'> {
  if (TIPOS_VEICULO_NO_CATALOGO.includes(tipo)) {
    return {
      veiculo: {
        idPerfil: (tipo === 'suv' ? 'sedanMedio' : 'hatchCompacto') as PerfilVeiculo['id'],
        posicaoFinal: 'sobreRodas' as const,
        estadosPecas: {},
        vidros: {},
        insulfilm: false,
        protecoes: { ...PROTECOES_INICIAIS },
      },
    }
  }
  if (tipo === 'vitima') {
    return {
      vitima: {
        idPerfil: 'adultoMasculino' as const,
        triagem: 'amarelo' as const,
        colarCervical: false,
        local: { tipo: 'solo' as const, postura: 'sentada' as const },
      },
    }
  }
  if (TIPOS_EQUIPAMENTO_NO_CATALOGO.includes(tipo)) {
    return { equipamento: { tipo: tipo as EquipamentoEstabilizacao } }
  }
  if (tipo === 'derramamento') {
    // mancha nova: sem vazamento ativo e aceitando os três materiais — o
    // instrutor ajusta no editor conforme a regra da aula
    return { risco: { vazamentoAtivo: false, materiaisAceitos: [...MATERIAIS_PADRAO_DERRAMAMENTO] } }
  }
  if (tipo === 'caminhaoBombeiros' || tipo === 'resgate') {
    return { cor: '#dc2626' }
  }
  return {}
}

/** Montar: bandeja de peças, arraste e gizmo livres. */
export type ModoTabletop = 'montar'

interface EstadoTabletop {
  cena: CenaTabletop
  historico: Historico<CenaTabletop>
  idSelecionado: string | null
  ferramenta: FerramentaTabletop
  modo: ModoTabletop
  /** cena capturada no início de um arrasto de gizmo; enquanto existir, `aplicar` não registra histórico passo a passo */
  cenaAntesDoArrasto: CenaTabletop | null
}

type AcaoReducer =
  | { tipo: 'aplicar'; aplicar: (cena: CenaTabletop) => CenaTabletop }
  | { tipo: 'redefinirTudo' }
  | { tipo: 'definirCena'; cena: CenaTabletop }
  | { tipo: 'selecionar'; id: string | null }
  | { tipo: 'ferramenta'; ferramenta: FerramentaTabletop }
  | { tipo: 'iniciarArrasto' }
  | { tipo: 'finalizarArrasto' }
  | { tipo: 'cancelarArrasto' }
  | { tipo: 'desfazer' }
  | { tipo: 'refazer' }

function reducer(estado: EstadoTabletop, acao: AcaoReducer): EstadoTabletop {
  switch (acao.tipo) {
    case 'aplicar': {
      const nova = acao.aplicar(estado.cena)
      if (nova === estado.cena) return estado
      // durante um arrasto, cada quadro do gizmo chama `aplicar`; o passo de
      // desfazer só é registrado uma vez, em `finalizarArrasto`
      if (estado.cenaAntesDoArrasto) return { ...estado, cena: nova }
      return { ...estado, cena: nova, historico: registrar(estado.historico, estado.cena) }
    }
    case 'definirCena':
      return {
        ...estado,
        cena: acao.cena,
        historico: historicoVazio(),
        idSelecionado: null,
        cenaAntesDoArrasto: null,
      }
    case 'redefinirTudo':
      // tudo volta ao começo: cenário padrão, sem histórico
      return estadoInicialPadrao()
    case 'selecionar':
      return { ...estado, idSelecionado: acao.id }
    case 'ferramenta':
      return { ...estado, ferramenta: acao.ferramenta }
    case 'iniciarArrasto':
      return estado.cenaAntesDoArrasto ? estado : { ...estado, cenaAntesDoArrasto: estado.cena }
    case 'finalizarArrasto': {
      const base = estado.cenaAntesDoArrasto
      if (!base) return estado
      if (base === estado.cena) return { ...estado, cenaAntesDoArrasto: null }
      // mosquetão ou polia soltos perto de um ponto de ancoragem se prendem nele (no mesmo passo de desfazer)
      const id = estado.idSelecionado
      const antes = id ? base.objetos.find((o) => o.id === id) : undefined
      const depois = id ? estado.cena.objetos.find((o) => o.id === id) : undefined
      const moveu = Boolean(antes && depois && antes.transformacao.posicao.some((v, i) => v !== depois.transformacao.posicao[i]))
      const cena = moveu ? encaixarPecaNoPontoMaisPerto(estado.cena, id!, novoIdTabletop) : estado.cena
      return { ...estado, cena, cenaAntesDoArrasto: null, historico: registrar(estado.historico, base) }
    }
    case 'cancelarArrasto':
      return estado.cenaAntesDoArrasto
        ? { ...estado, cena: estado.cenaAntesDoArrasto, cenaAntesDoArrasto: null }
        : estado
    case 'desfazer': {
      const resultado = desfazerHistorico(estado.historico, estado.cena)
      if (!resultado) return estado
      return { ...estado, cena: resultado.estado, historico: resultado.historico, idSelecionado: null }
    }
    case 'refazer': {
      const resultado = refazerHistorico(estado.historico, estado.cena)
      if (!resultado) return estado
      return { ...estado, cena: resultado.estado, historico: resultado.historico, idSelecionado: null }
    }
  }
}

function estadoInicialPadrao(cena: CenaTabletop = criarCenaPadrao()): EstadoTabletop {
  return {
    cena,
    historico: historicoVazio(),
    idSelecionado: null,
    ferramenta: 'translate',
    modo: 'montar',
    cenaAntesDoArrasto: null,
  }
}

function estadoInicial(): EstadoTabletop {
  return estadoInicialPadrao(carregarTabletopLocal())
}

export interface ResultadoImportacao {
  sucesso: boolean
  mensagem: string
}

/**
 * Todo o estado do Table top: cena, seleção, ferramenta e histórico de
 * desfazer/refazer, com salvamento automático no navegador.
 *
 * Concentra aqui o que antes eram ~15 `useState`/`useCallback` soltos no
 * `App`, no mesmo espírito de `extracao` (App.tsx) ser um cálculo só: as
 * ações já devolvem a cena pronta, e os componentes só leem o resultado.
 */
export function useTabletop({ mesaCompartilhada = false }: { mesaCompartilhada?: boolean } = {}) {
  const [estado, despachar] = useReducer(reducer, undefined, estadoInicial)
  const cena = estado.cena
  const cenaParaSalvar = cena

  // autosave com debounce — o editor continua utilizável se o navegador
  // bloquear o armazenamento (ex.: modo privado)
  useEffect(() => {
    const agendamento = window.setTimeout(() => {
      try {
        window.localStorage.setItem(CHAVE_TABLETOP_LOCAL, serializarCenaTabletop(cenaParaSalvar))
      } catch {
        // sem persistência; a sessão continua funcionando normalmente
      }
    }, 250)
    return () => window.clearTimeout(agendamento)
  }, [cenaParaSalvar])

  const aplicar = useCallback((fn: (cena: CenaTabletop) => CenaTabletop) => {
    despachar({ tipo: 'aplicar', aplicar: fn })
  }, [])

  const [idOlhandoPara, setIdOlhandoPara] = useState<string | null>(null)
  const [asfaltoAtivo, setAsfaltoAtivo] = useState(true)
  const alternarAsfalto = useCallback(() => setAsfaltoAtivo((prev) => !prev), [])

  const selecionar = useCallback((id: string | null) => despachar({ tipo: 'selecionar', id }), [])

  const escolherFerramenta = useCallback(
    (ferramenta: FerramentaTabletop) => { setIdOlhandoPara(null); despachar({ tipo: 'ferramenta', ferramenta }) },
    [],
  )
  const iniciarManipulacao = useCallback(() => despachar({ tipo: 'iniciarArrasto' }), [])
  const finalizarManipulacao = useCallback(() => despachar({ tipo: 'finalizarArrasto' }), [])
  const aoAlterarManipulacao = useCallback(
    (ativa: boolean) => despachar({ tipo: ativa ? 'iniciarArrasto' : 'finalizarArrasto' }),
    [],
  )
  const cancelarManipulacao = useCallback(() => despachar({ tipo: 'cancelarArrasto' }), [])

  const desfazer = useCallback(() => despachar({ tipo: 'desfazer' }), [])
  const refazer = useCallback(() => despachar({ tipo: 'refazer' }), [])

  const adicionar = useCallback(
    (tipo: TipoObjetoTabletop) => {
      const id = novoIdTabletop()
      const selecionadoAtual = cena.objetos.find((o) => o.id === estado.idSelecionado)

      aplicar((c) => {
        // com qualquer objeto selecionado, o novo nasce ao lado dele — não
        // empilhado exatamente na mesma posição do anterior
        const posicao: TransformacaoTabletop['posicao'] = selecionadoAtual
          ? [selecionadoAtual.transformacao.posicao[0] + 1.6, 0, selecionadoAtual.transformacao.posicao[2]]
          : [0, 0, 0]
        return adicionarObjeto(
          c,
          { tipo, transformacao: { ...TRANSFORMACAO_TABLETOP_PADRAO, posicao }, ...blocoPadraoTabletop(tipo) },
          () => id,
        )
      })
      selecionar(id)
      escolherFerramenta('translate')
    },
    [aplicar, selecionar, escolherFerramenta, cena.objetos, estado.idSelecionado],
  )

  /** Acomoda a polia na corda que passa pela roldana dela (ver `alinharPoliaComCorda`). */
  const alinharPolia = useCallback((idObjeto: string) => aplicar((c) => alinharPoliaComCorda(c, idObjeto)), [aplicar])

  /** Uma fita em volta do tronco da peça (árvore, poste, toco), já com o diâmetro do tronco, e selecionada. */
  const envolverComFita = useCallback(
    (idObjeto: string) => {
      const objeto = cena.objetos.find((o) => o.id === idObjeto)
      const novo = objeto ? novoEnvolvimentoEm(objeto) : null
      if (!novo) return
      const id = novoIdTabletop()
      aplicar((c) => adicionarObjeto(c, novo, () => id))
      selecionar(id)
    },
    [aplicar, selecionar, cena.objetos],
  )

  const transformar = useCallback(
    (id: string, transformacao: TransformacaoTabletop) =>
      aplicar((c) => {
        const obj = c.objetos.find((o) => o.id === id)
        const nova = atualizarTransformacao(c, id, transformacao)
        if (obj?.tipo === 'trabalhador' && transformacao.posicao) {
          return atualizarObjeto(nova, id, { posicaoDeSoltura: transformacao.posicao })
        }
        return nova
      }),
    [aplicar],
  )

  const resetarPosicaoSoltura = useCallback(
    (id: string) => {
      aplicar((c) => {
        const obj = c.objetos.find((o) => o.id === id)
        if (!obj || !obj.posicaoDeSoltura) return c
        return atualizarTransformacao(c, id, { posicao: [...obj.posicaoDeSoltura] })
      })
    },
    [aplicar],
  )

  // --- B2: peça da bandeja aguardando um toque no chão para nascer ali -----
  const [tipoParaPosicionar, setTipoParaPosicionar] = useState<TipoObjetoTabletop | null>(null)

  const iniciarPosicionamento = useCallback((tipo: TipoObjetoTabletop) => {
    setIdOlhandoPara(null)
    setTipoParaPosicionar(tipo)
  }, [])

  const cancelarPosicionamento = useCallback(() => setTipoParaPosicionar(null), [])

  /** Cria a peça onde foi tocado; encaixe de 0,25 m é opcional; suporta altura Y e rotação em superfícies. */
  const adicionarNaPosicao = useCallback(
    (
      tipo: TipoObjetoTabletop,
      x: number,
      z: number,
      encaixeAtivo = false,
      y = 0,
      rotacao?: readonly [number, number, number],
    ) => {
      const id = novoIdTabletop()
      const [xEncaixado, zEncaixado] = encaixeAtivo ? encaixarPosicaoXZ(x, z) : [x, z]
      const padrao = blocoPadraoTabletop(tipo)
      const bloco = tipo === 'vitima' && y > 0.05 && padrao.vitima
        ? { ...padrao, vitima: { ...padrao.vitima, local: { tipo: 'solo' as const, postura: 'decubitoDorsal' as const } } }
        : padrao
      aplicar((c) =>
        adicionarObjeto(
          c,
          {
            tipo,
            transformacao: {
              ...TRANSFORMACAO_TABLETOP_PADRAO,
              posicao: [xEncaixado, y, zEncaixado],
              rotacao: rotacao ? [...rotacao] : TRANSFORMACAO_TABLETOP_PADRAO.rotacao,
            },
            ...bloco,
          },
          () => id,
        ),
      )
      selecionar(id)
      setTipoParaPosicionar(null)
    },
    [aplicar, selecionar],
  )

  const duplicar = useCallback(
    (id: string) => {
      const novoId = novoIdTabletop()
      aplicar((c) => duplicarObjeto(c, id, () => novoId))
      selecionar(novoId)
    },
    [aplicar, selecionar],
  )

  /** Remover um veículo solta as vítimas vinculadas a ele no chão, na posição de mundo em que estavam. */
  const remover = useCallback(
    (id: string) => {
      aplicar((c) => removerObjeto(soltarVitimasDoVeiculoRemovido(c, id), id))
      selecionar(null)
    },
    [aplicar, selecionar],
  )

  const renomearObjeto = useCallback(
    (id: string, nome: string) => aplicar((c) => atualizarObjeto(c, id, { nome })),
    [aplicar],
  )
  const alterarCor = useCallback(
    (id: string, cor: string) => aplicar((c) => atualizarObjeto(c, id, { cor })),
    [aplicar],
  )
  const resetarTransformacao = useCallback(
    (id: string) => aplicar((c) => atualizarTransformacao(c, id, TRANSFORMACAO_TABLETOP_PADRAO)),
    [aplicar],
  )
  const alternarBloqueio = useCallback(
    (id: string, bloqueado: boolean) => aplicar((c) => atualizarObjeto(c, id, { bloqueado })),
    [aplicar],
  )
  const alternarOculto = useCallback(
    (id: string, oculto: boolean) => aplicar((c) => atualizarObjeto(c, id, { oculto })),
    [aplicar],
  )

  const renomearCenaTabletop = useCallback((nome: string) => aplicar((c) => renomearCena(c, nome)), [aplicar])

  // Table top em branco: começa em Montar, é o ponto de partida de uma cena nova.
  const criarCena = useCallback(() => {
    setIdOlhandoPara(null)
    despachar({ tipo: 'definirCena', cena: criarCenaVazia() })
  }, [mesaCompartilhada])

  // Redefinir tudo: apaga a cena guardada no navegador e volta ao cenário padrão, como no primeiro acesso.
  const redefinirTudo = useCallback(() => {
    setIdOlhandoPara(null)
    setAsfaltoAtivo(true)
    apagarTabletopLocal()
    despachar({ tipo: 'redefinirTudo' })
  }, [])

  // Cenário pronto (preset dos slides ou o exemplo complexo).
  const carregarExemplo = useCallback((idPreset?: string) => {
    setIdOlhandoPara(null)
    // técnica de salvamento: abre em Montar, porque o instrutor vai mexer nas cordas e nos objetos
    const cenaSalvamento = idPreset ? criarCenaPresetSalvamento(idPreset) : undefined
    if (cenaSalvamento) {
      despachar({ tipo: 'definirCena', cena: cenaSalvamento })
      return
    }
    const preset = idPreset ? obterPresetPorId(idPreset) : null
    const novaCena = preset ? preset.criarCena() : criarPresetTabletopComplexo()
    despachar({ tipo: 'definirCena', cena: novaCena })
  }, [mesaCompartilhada])

  // Montagem salva pelo instrutor: como as técnicas de salvamento, abre em Montar para editar.
  const abrirCenaMontada = useCallback((cena: CenaTabletop) => {
    setIdOlhandoPara(null)
    despachar({ tipo: 'definirCena', cena: acomodarPoliasNasCordas(cena) })
  }, [mesaCompartilhada])

  const importar = useCallback((conteudo: string): ResultadoImportacao => {
    try {
      const importada = acomodarPoliasNasCordas(desserializarCenaTabletop(conteudo))
      setIdOlhandoPara(null)
      // um table top importado normalmente já foi montado em outra sessão
      despachar({ tipo: 'definirCena', cena: importada })
      return { sucesso: true, mensagem: 'Table top importado com sucesso.' }
    } catch (erro) {
      return {
        sucesso: false,
        mensagem: erro instanceof Error ? erro.message : 'Arquivo de table top inválido.',
      }
    }
  }, [mesaCompartilhada])

  // --- vítimas: dentro do carro (em qualquer âncora livre) ou fora dele ---

  const vincularVitima = useCallback(
    (idVitima: string, idVeiculo: string, idAncora: string) =>
      aplicar((c) => vincularVitimaAoVeiculo(c, idVitima, idVeiculo, idAncora)),
    [aplicar],
  )
  const desvincularVitima = useCallback(
    (idVitima: string, postura?: PosturaVitimaSolo) => aplicar((c) => desvincularParaOSolo(c, idVitima, postura)),
    [aplicar],
  )
  const moverNaFila = useCallback(
    (idVitima: string, direcao: 'subir' | 'descer') => aplicar((c) => moverNaFilaDominio(c, idVitima, direcao)),
    [aplicar],
  )

  const atualizarVitima = useCallback(
    (id: string, alteracoes: Partial<BlocoVitimaTabletop>) => aplicar((c) => atualizarObjeto(c, id, { vitima: alteracoes })),
    [aplicar],
  )

  const atualizarRisco = useCallback(
    (id: string, alteracoes: Partial<BlocoRiscoTabletop>) => aplicar((c) => atualizarObjeto(c, id, { risco: alteracoes })),
    [aplicar],
  )

  /**
   * Muda as medidas de um objeto (ângulo da escada, altura do toco...). O valor
   * é preso ao intervalo do parâmetro; chave que o tipo não tem é ignorada.
   * Para um controle contínuo, envolva a edição em `aoAlterarManipulacao` e ela
   * vira um passo só de desfazer.
   */
  const atualizarParametros = useCallback(
    (id: string, parametros: ParametrosTabletop) =>
      aplicar((c) => {
        const objeto = c.objetos.find((o) => o.id === id)
        if (!objeto) return c
        const validos: Record<string, number> = {}
        for (const [chave, valor] of Object.entries(parametros)) {
          const preso = clamparParametro(objeto.tipo, chave, valor)
          if (preso !== undefined) validos[chave] = preso
        }
        return Object.keys(validos).length === 0 ? c : atualizarObjeto(c, id, { parametros: validos })
      }),
    [aplicar],
  )

  const atualizarVeiculo = useCallback(
    (id: string, alteracoes: Partial<BlocoVeiculoTabletop>) => aplicar((c) => atualizarObjeto(c, id, { veiculo: alteracoes })),
    [aplicar],
  )

  /**
   * Trocar a posição final não é só gravar o campo: a rotação e a altura de
   * apoio do objeto mudam junto (mantendo a guinada escolhida), do mesmo
   * jeito que o botão de posição final descreve no painel.
   */
  const definirPosicaoFinal = useCallback(
    (id: string, posicaoFinal: PosicaoFinalVeiculo) =>
      aplicar((c) => {
        const objeto = c.objetos.find((o) => o.id === id)
        if (!objeto?.veiculo) return c
        const perfil = perfilVeiculoPorId(objeto.veiculo.idPerfil)
        const guinadaAtual = objeto.transformacao.rotacao[1]
        const pose = poseDaPosicaoFinal(perfil, posicaoFinal, guinadaAtual)
        const comBloco = atualizarObjeto(c, id, { veiculo: { posicaoFinal } })
        return atualizarObjeto(comBloco, id, {
          transformacao: { rotacao: pose.rotacao, posicao: [objeto.transformacao.posicao[0], pose.altura, objeto.transformacao.posicao[2]] },
        })
      }),
    [aplicar],
  )

  // guarda a mensagem didática da última ação de vidro, para o painel exibir
  // sem precisar recalcular a mesma ação por conta própria
  const [ultimoResultadoVidro, setUltimoResultadoVidro] = useState<ResultadoAcaoVidro | null>(null)

  const executarManejoVidro = useCallback(
    (idVeiculo: string, idVidro: IdVidro, ferramenta: FerramentaVidro) => {
      const resultado = executarManejoVidroNoVeiculo(cena, idVeiculo, idVidro, ferramenta)
      setUltimoResultadoVidro(resultado?.resultado ?? null)
      if (resultado?.resultado?.sucesso) {
        tocarSomEstilhacoVidro()
      }
      if (resultado) aplicar(() => resultado.cena)
    },
    [aplicar, cena],
  )

  const restaurarVidros = useCallback(
    (idVeiculo: string) => {
      setUltimoResultadoVidro(null)
      aplicar((c) => restaurarVidrosDoVeiculo(c, idVeiculo))
    },
    [aplicar],
  )

  const [ultimoResultadoCorte, setUltimoResultadoCorte] = useState<ResultadoIntervencaoVeiculoTabletop | null>(null)

  const aplicarDeformacaoVeiculo = useCallback(
    (idVeiculo: string, nivel: NivelDanoVeicular | 'nenhum', intrusaoM?: number,
      regiao: RegiaoDanoVeicular = 'frente', idPorta?: IdPortaTabletop) => {
      aplicar((c) => {
        const perfilId = c.objetos.find((o) => o.id === idVeiculo)?.veiculo?.idPerfil
        if (!perfilId) return c
        const deformacao = nivel === 'nenhum'
          ? null
          : idPorta
            ? deformacaoPredefinidaNaPorta(perfilVeiculoPorId(perfilId), idPorta, nivel, intrusaoM)
            : {
                ...calcularDeformacaoFrontalPredefinida(nivel),
                regiao,
                ...(intrusaoM !== undefined ? { intrusaoM } : {}),
              }
        return aplicarDeformacaoNaRegiao(c, idVeiculo, regiao, deformacao, idPorta)
      })
    },
    [aplicar],
  )

  const aplicarAmassadoNoPonto = useCallback((idVeiculo: string, ponto: Readonly<{ x: number; y: number; z: number }>, larguraM: number, intrusaoM: number) => {
    aplicar((c) => adicionarAmassadoNoPonto(c, idVeiculo, ponto, larguraM, intrusaoM))
  }, [aplicar])

  const limparAmassados = useCallback((idVeiculo: string) => {
    aplicar((c) => limparAmassadosDoVeiculo(c, idVeiculo))
  }, [aplicar])

  const executarCorteVeiculo = useCallback(
    (idVeiculo: string, idPeca: string, novoEstado: EstadoCortePecaTabletop) => {
      const res = executarCorteNoVeiculo(cena, idVeiculo, idPeca, novoEstado)
      setUltimoResultadoCorte(res.resultado)
      if (res.resultado.sucesso) {
        tocarSomCorteMetal()
      }
      aplicar(() => res.cena)
    },
    [aplicar, cena],
  )

  const selecionarPecaVeiculo = useCallback(
    (idVeiculo: string, idPeca: string | null) => {
      aplicar((c) => selecionarPecaNoVeiculo(c, idVeiculo, idPeca))
    },
    [aplicar],
  )

  const restaurarEstruturaVeiculo = useCallback(
    (idVeiculo: string) => {
      setUltimoResultadoCorte(null)
      aplicar((c) => restaurarEstruturaDoVeiculo(c, idVeiculo))
    },
    [aplicar],
  )

  // "Testar estabilidade" é uma ação explícita — selecionar o veículo não o balança
  const [pedidoTesteBalanco, setPedidoTesteBalanco] = useState<PedidoTesteBalanco | null>(null)
  const testarBalanco = useCallback((idVeiculo: string) => {
    setPedidoTesteBalanco((anterior) => ({ idVeiculo, serial: (anterior?.serial ?? 0) + 1 }))
  }, [])

  // Cenário travado: veículos e obstáculos fixos, apenas ferramentas/equipamentos manipuláveis
  const [cenarioTravado, setCenarioTravado] = useState(false)
  const alternarTravaCenario = useCallback(() => setCenarioTravado((anterior) => !anterior), [])

  const ancorasLivres = useCallback(
    (idVeiculo: string) => {
      const veiculo = cena.objetos.find((o) => o.id === idVeiculo)
      return veiculo ? ancorasLivresDoVeiculo(veiculo, cena.objetos) : []
    },
    [cena.objetos],
  )

  // corda e ancoragem se excluem: um desenho cancela o outro e uma seleção tira a outra.
  // Os dois hooks se chamam, então a ligação passa por ponteiros preenchidos a cada render.
  const cancelarAncoragemRef = useRef<() => void>(() => {})
  const limparAncoragemSelecionadaRef = useRef<() => void>(() => {})
  const cancelarCordaRef = useRef<() => void>(() => {})
  const limparCordaSelecionadaRef = useRef<() => void>(() => {})

  const cordas = useCordasTabletop({
    cena,
    aplicar,
    gerarId: novoIdTabletop,
    idObjetoSelecionado: estado.idSelecionado,
    selecionarObjeto: selecionar,
    cancelarPosicionamento,
    ativo: estado.modo === 'montar',
    aoIniciarDesenho: () => cancelarAncoragemRef.current(),
    aoSelecionar: () => limparAncoragemSelecionadaRef.current(),
  })
  const ancoragens = useAncoragensTabletop({
    cena,
    aplicar,
    gerarId: novoIdTabletop,
    idObjetoSelecionado: estado.idSelecionado,
    selecionarObjeto: selecionar,
    cancelarPosicionamento,
    ativo: estado.modo === 'montar',
    aoIniciarDesenho: () => cancelarCordaRef.current(),
    aoSelecionar: () => limparCordaSelecionadaRef.current(),
  })
  cancelarAncoragemRef.current = ancoragens.cancelarAncoragem
  limparAncoragemSelecionadaRef.current = () => ancoragens.selecionarAncoragem(null)
  cancelarCordaRef.current = cordas.cancelarCorda
  limparCordaSelecionadaRef.current = () => cordas.selecionarCorda(null)

  // Etapa "pega e coloca": tocar num ponto de ancoragem (com a peça selecionada)
  // abre ali as ações do ponto: prender corda, prender fita, clipar mosquetão, pendurar polia.
  const [pontoEscolhido, setPontoEscolhido] = useState<ReferenciaPonto | null>(null)
  const [fitaParaJuntar, setFitaParaJuntar] = useState<ReferenciaPonto | null>(null)
  useEffect(() => {
    // o ponto some quando se muda de modo, se desenha uma corda ou ancoragem, ou a peça deixa de existir
    if (estado.modo !== 'montar' || cordas.desenhoCorda || ancoragens.desenhoAncoragem) {
      setPontoEscolhido(null)
      setFitaParaJuntar(null)
    }
  }, [estado.modo, cordas.desenhoCorda, ancoragens.desenhoAncoragem])
  useEffect(() => {
    setPontoEscolhido((atual) => (atual && !cena.objetos.some((o) => o.id === atual.idObjeto) ? null : atual))
  }, [cena.objetos])
  const centroDaAncoragemSelecionada = ancoragens.ancoragemSelecionada?.centro ?? null
  useEffect(() => {
    // outra peça (ou uma corda) selecionada: o ponto da peça anterior deixa de estar escolhido;
    // o centro da ancoragem tocada fica (as ações dele aparecem com ela)
    setPontoEscolhido((atual) =>
      atual &&
      atual.idObjeto !== estado.idSelecionado &&
      !(centroDaAncoragemSelecionada && atual.idObjeto === centroDaAncoragemSelecionada.idObjeto && atual.idPonto === centroDaAncoragemSelecionada.idPonto)
        ? null
        : atual,
    )
  }, [estado.idSelecionado, cordas.idCordaSelecionada, centroDaAncoragemSelecionada])
  const escolherPonto = useCallback(
    (referencia: ReferenciaPonto | null) => {
      if (fitaParaJuntar && referencia) {
        if (podeJuntarFitas(cena, fitaParaJuntar, referencia)) {
          const ids = [novoIdTabletop(), novoIdTabletop()]
          aplicar((c) => {
            let k = 0
            return juntarFitasEmPonto(c, fitaParaJuntar, referencia, () => ids[k++]!).cena
          })
          setFitaParaJuntar(null)
          const novoPonto: ReferenciaPonto = { idObjeto: ids[0]!, idPonto: 'olhal' }
          setPontoEscolhido(novoPonto)
          selecionar(ids[0]!)
          return
        }
      }
      setPontoEscolhido(referencia)
    },
    [aplicar, cena, fitaParaJuntar, selecionar],
  )
  const iniciarJuntarFita = useCallback((ref: ReferenciaPonto) => {
    setFitaParaJuntar(ref)
    setPontoEscolhido(null)
  }, [])
  const cancelarJuntarFitas = useCallback(() => {
    setFitaParaJuntar(null)
  }, [])
  /**
   * Tocar numa ancoragem (V, W, M) na cena: ela fica selecionada e o ponto
   * central já abre as ações dele — prender corda, pendurar polia, montar o
   * sistema de vantagem — sem precisar ligar os pontos de ancoragem.
   */
  const tocarAncoragem = useCallback(
    (id: string | null) => {
      ancoragens.selecionarAncoragem(id)
      const ancoragem = id ? cena.ancoragens?.find((a) => a.id === id) : undefined
      if (ancoragem && estado.modo === 'montar') setPontoEscolhido(ancoragem.centro)
    },
    [ancoragens, cena.ancoragens, estado.modo],
  )
  const prenderCordaNoPonto = useCallback(
    (funcao: 'livre' | 'fita') => {
      if (!pontoEscolhido) return
      cordas.iniciarCorda(funcao, pontoEscolhido)
      setPontoEscolhido(null)
    },
    [cordas, pontoEscolhido],
  )
  /** Prende a peça no ponto: uma alteração só da cena; a peça nova fica selecionada. */
  const prenderPecaEm = useCallback(
    (ponto: ReferenciaPonto, peca: PecaParaPrender, parametros?: Record<string, number>) => {
      // os ids saem antes: o reducer pode rodar esta função depois (ou duas vezes, no modo estrito)
      const ids = [novoIdTabletop(), novoIdTabletop()]
      try {
        let k = 0
        prenderPecaNoPonto(cena, ponto, peca, () => ids[k++]!, parametros)
      } catch {
        return null
      }
      aplicar((c) => {
        let k = 0
        try {
          return prenderPecaNoPonto(c, ponto, peca, () => ids[k++]!, parametros).cena
        } catch {
          return c
        }
      })
      // a peça nova fica selecionada: as opções dela aparecem junto dela
      selecionar(ids[0]!)
      return ids[0]!
    },
    [aplicar, cena, selecionar],
  )
  const prenderPecaNoPontoEscolhido = useCallback(
    (peca: PecaParaPrender, parametros?: Record<string, number>) => {
      if (!pontoEscolhido) return
      const ponto = pontoEscolhido
      setPontoEscolhido(null)
      prenderPecaEm(ponto, peca, parametros)
    },
    [pontoEscolhido, prenderPecaEm],
  )
  const prenderNoComCordaNoPontoEscolhido = useCallback(
    (no: 'noOito' | 'noFiel') => {
      if (!pontoEscolhido) return
      const ponto = pontoEscolhido
      setPontoEscolhido(null)
      const idPeca = prenderPecaEm(ponto, no)
      if (idPeca) {
        const idPontoNo = no === 'noOito' ? 'passagem' : 'olhal'
        cordas.iniciarCorda('livre', { idObjeto: idPeca, idPonto: idPontoNo })
      }
    },
    [cordas, pontoEscolhido, prenderPecaEm],
  )

  /**
   * Tocar num ponto de ancoragem com uma peça da bandeja na mão: o mosquetão, a
   * polia e o nó fiel se prendem nele (numa estrutura, o mosquetão e a polia por
   * uma fita); as outras peças nascem no lugar do ponto, no ar se for preciso.
   */
  const posicionarNoPonto = useCallback(
    (ponto: ReferenciaPonto) => {
      if (!tipoParaPosicionar) return
      const tipo = tipoParaPosicionar
      // o sistema de vantagem pende pelo olhal do bloco fixo, como a polia: numa estrutura, por uma fita
      if (tipo === 'mosquetao' || tipo === 'polia' || tipo === 'noFiel' || tipo === 'sistemaVM') {
        if (prenderPecaEm(ponto, tipo)) setTipoParaPosicionar(null)
        return
      }
      const mundo = localizarPontoNoMundo(cena, ponto)
      if (mundo) adicionarNaPosicao(tipo, mundo[0], mundo[2], false, mundo[1])
    },
    [adicionarNaPosicao, cena, prenderPecaEm, tipoParaPosicionar],
  )
  /** Uma fita entre duas estruturas vira o V, com o mosquetão no vértice. */
  const mosquetaoNoVDaFita = useCallback(
    (idFita: string) => {
      const ids = Array.from({ length: 6 }, () => novoIdTabletop())
      let idMosquetao: string
      try {
        let k = 0
        idMosquetao = fitaEmVComMosquetao(cena, idFita, () => ids[k++]!).idMosquetao
      } catch {
        return
      }
      aplicar((c) => {
        let k = 0
        try {
          return fitaEmVComMosquetao(c, idFita, () => ids[k++]!).cena
        } catch {
          return c
        }
      })
      cordas.selecionarCorda(null)
      selecionar(idMosquetao)
    },
    [aplicar, cena, cordas, selecionar],
  )

  const objetoSelecionado: ObjetoTabletop | null = useMemo(
    () => cena.objetos.find((o) => o.id === estado.idSelecionado) ?? null,
    [cena.objetos, estado.idSelecionado],
  )

  const fila = useMemo(() => filaDeAtendimento(cena.objetos), [cena.objetos])

  const cancelarOlharPara = useCallback(() => setIdOlhandoPara(null), [])
  useEffect(() => {
    if (idOlhandoPara && !cena.objetos.some(o => o.id === idOlhandoPara)) cancelarOlharPara()
  }, [cena.objetos, idOlhandoPara, cancelarOlharPara])

  const podeVirar = (o: ObjetoTabletop) => !o.bloqueado && (!cenarioTravado || Boolean(o.equipamento))
  const definirDirecao = (id: string, graus: number) => aplicar(c => {
    const objeto = c.objetos.find(o => o.id === id)
    if (!objeto || !podeVirar(objeto) || objeto.vitima?.local.tipo === 'veiculo') return c
    const t = comDirecao(objeto.transformacao, graus)
    if (t.rotacao[1] === objeto.transformacao.rotacao[1]) return c
    return atualizarTransformacao(c, id, t)
  })
  const girar = (id: string, delta: number) => {
    const objeto = cena.objetos.find(o => o.id === id)
    if (objeto) definirDirecao(id, objeto.transformacao.rotacao[1] * 180 / Math.PI + delta)
  }
  const iniciarOlharPara = (id: string) => {
    const objeto = cena.objetos.find(o => o.id === id)
    if (!objeto || !podeVirar(objeto) || !['bombeiro', 'bombeiroMedico'].includes(objeto.tipo)) return
    cancelarPosicionamento()
    selecionar(id)
    escolherFerramenta('translate')
    setIdOlhandoPara(id)
  }
  const selecionarNaMesa = (id: string | null) => {
    if (!idOlhandoPara) { selecionar(id); return }
    const origem = cena.objetos.find(o => o.id === idOlhandoPara)
    const alvo = cena.objetos.find(o => o.id === id)
    if (!origem || !alvo || origem.id === alvo.id || (!alvo.vitima && !['carro', 'suv', 'caminhao', 'caminhaoBombeiros', 'resgate', 'moto'].includes(alvo.tipo))) return
    const direcao = direcaoParaAlvo(cena, origem, alvo)
    if (direcao === null) return
    definirDirecao(origem.id, direcao)
    cancelarOlharPara()
  }
  const alterarHistoria = (historiaInicial: string) => aplicar(c =>
    c.historiaInicial === historiaInicial ? c : { ...c, historiaInicial })
  const marcarItemDoChecklistAltura = (idItem: string, marcacao: MarcacaoChecklistAltura | null) =>
    aplicar(c => marcarItemChecklistAltura(c, idItem, marcacao))

  return {
    cena,
    idSelecionado: estado.idSelecionado,
    objetoSelecionado,
    ferramenta: estado.ferramenta,
    modo: estado.modo,
    mesaCompartilhada,
    alterarHistoria,
    marcarItemDoChecklistAltura,
    envolverComFita,
    alinharPolia,
    girar,
    definirDirecao,
    idOlhandoPara,
    iniciarOlharPara,
    cancelarOlharPara,
    selecionarNaMesa,
    fila,
    podeDesfazer: podeDesfazerHistorico(estado.historico),
    podeRefazer: podeRefazerHistorico(estado.historico),
    selecionar,
    escolherFerramenta,
    iniciarManipulacao,
    finalizarManipulacao,
    aoAlterarManipulacao,
    cancelarManipulacao,
    desfazer,
    refazer,
    adicionar,
    transformar,
    tipoParaPosicionar,
    iniciarPosicionamento,
    cancelarPosicionamento,
    adicionarNaPosicao,
    duplicar,
    remover,
    renomearObjeto,
    alterarCor,
    resetarTransformacao,
    resetarPosicaoSoltura,
    alternarBloqueio,
    alternarOculto,
    renomearCena: renomearCenaTabletop,
    criarCena,
    redefinirTudo,
    carregarExemplo,
    abrirCenaMontada,
    importar,
    vincularVitima,
    desvincularVitima,
    moverNaFila,
    ancorasLivres,
    atualizarVitima,
    atualizarRisco,
    atualizarParametros,
    atualizarVeiculo,
    definirPosicaoFinal,
    executarManejoVidro,
    restaurarVidros,
    ultimoResultadoVidro,
    aplicarDeformacaoVeiculo,
    aplicarAmassadoNoPonto,
    limparAmassados,
    executarCorteVeiculo,
    selecionarPecaVeiculo,
    restaurarEstruturaVeiculo,
    ultimoResultadoCorte,
    pedidoTesteBalanco,
    testarBalanco,
    asfaltoAtivo,
    alternarAsfalto,
    setAsfaltoAtivo,
    cenarioTravado,
    alternarTravaCenario,
    setCenarioTravado,
    ...cordas,
    ...ancoragens,
    pontoEscolhido,
    escolherPonto,
    tocarAncoragem,
    prenderCordaNoPonto,
    prenderPecaNoPontoEscolhido,
    prenderNoComCordaNoPontoEscolhido,
    fitaParaJuntar,
    iniciarJuntarFita,
    cancelarJuntarFitas,
    posicionarNoPonto,
    mosquetaoNoVDaFita,
  }
}




